import { useCallback, useRef, useState } from "react";
import { Loader2, Sparkles, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { isHeicImage, normalizeImageFile } from "@/lib/heic";
import { track } from "@/lib/posthog";

interface RefinePanelProps {
  used: number;
  limit: number;
  isSubmitting: boolean;
  onSubmit: (params: { promptText: string; referenceImageDataUrl?: string | null }) => Promise<void>;
  designId: string;
  /** Hide the card chrome + header (used inside the refine sheet, which has its own header). */
  compact?: boolean;
  onSuccess?: () => void;
}

const MAX_BYTES = 8 * 1024 * 1024;

const RefinePanel = ({ used, limit, isSubmitting, onSubmit, designId, compact = false, onSuccess }: RefinePanelProps) => {
  const [text, setText] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const [dataUrl, setDataUrl] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isConverting, setIsConverting] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const exhausted = used >= limit;
  const remaining = Math.max(0, limit - used);

  const handleFile = useCallback(async (rawFile: File) => {
    if (rawFile.size > MAX_BYTES) {
      toast({ title: "Image too large", description: "Max 8 MB.", variant: "destructive" });
      return;
    }
    const isHeic = await isHeicImage(rawFile);
    if (!rawFile.type.startsWith("image/") && !isHeic) {
      toast({ title: "Unsupported file", description: "Upload JPG, PNG, or HEIC.", variant: "destructive" });
      return;
    }
    setIsConverting(true);
    try {
      const file = await normalizeImageFile(rawFile);
      const reader = new FileReader();
      reader.onload = (e) => {
        const url = e.target?.result as string;
        setDataUrl(url);
        setPreview(url);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast({
        title: "Couldn't load image",
        description: err instanceof Error ? err.message : "Try JPG or PNG.",
        variant: "destructive",
      });
    } finally {
      setIsConverting(false);
    }
  }, []);

  const clearImage = () => {
    setDataUrl(null);
    setPreview(null);
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleSubmit = async () => {
    const trimmed = text.trim();
    if (!trimmed && !dataUrl) {
      toast({ title: "Add feedback or an image", description: "Type a tweak or attach an inspiration image.", variant: "destructive" });
      return;
    }
    track("design_refinement_submitted", {
      design_id: designId,
      has_reference_image: !!dataUrl,
      prompt_length: trimmed.length,
      used_before: used,
    });
    try {
      await onSubmit({ promptText: trimmed, referenceImageDataUrl: dataUrl ?? undefined });
      setText("");
      clearImage();
      toast({ title: "Refinement ready", description: "Added to your refinements strip." });
      onSuccess?.();
    } catch (e) {
      toast({
        title: "Refinement failed",
        description: e instanceof Error ? e.message : "Please try again.",
        variant: "destructive",
      });
    }
  };


  return (
    <div className={compact ? "space-y-3" : "border border-border bg-card p-4 sm:p-5 space-y-3"}>
      {!compact && (
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-base text-foreground flex items-center gap-1.5">
              <Sparkles className="w-4 h-4 text-primary" />
              Refine this design
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              Tweak the look without burning a monthly generation.
            </p>
          </div>
          <span className="text-xs text-muted-foreground whitespace-nowrap">
            {used}/{limit} tokens used this month
          </span>
        </div>
      )}

      <Textarea
        value={text}
        maxLength={1000}
        rows={compact ? 2 : 3}
        disabled={exhausted || isSubmitting}
        placeholder="Swap the rug for something warmer, add a reading nook… or just drop an inspiration image."
        onChange={(e) => setText(e.target.value.slice(0, 1000))}
        className="resize-none"
      />
      <div className="text-right text-[10px] text-muted-foreground -mt-1">{text.length}/1000</div>

      <div className="flex flex-wrap items-center gap-2 sm:gap-3">

        {preview ? (
          <div className="relative">
            <img src={preview} alt="Reference" className="w-16 h-16 object-cover border border-border" />
            <button
              type="button"
              onClick={clearImage}
              className="absolute -top-1.5 -right-1.5 bg-foreground text-background rounded-full p-0.5"
              aria-label="Remove reference"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        ) : (
          <label
            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={(e) => {
              e.preventDefault();
              setIsDragging(false);
              const file = e.dataTransfer.files[0];
              if (file) void handleFile(file);
            }}
            className={`flex items-center gap-2 px-3 py-2 border border-dashed cursor-pointer text-xs text-muted-foreground transition-colors ${
              isDragging ? "border-foreground bg-foreground/5" : "border-border hover:border-foreground/40"
            } ${exhausted || isSubmitting ? "opacity-50 pointer-events-none" : ""}`}
          >
            <input
              ref={inputRef}
              type="file"
              accept="image/*,.heic,.heif"
              className="sr-only"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) void handleFile(file);
              }}
            />
            {isConverting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
            <span>{isConverting ? "Converting…" : "Add inspiration image (optional)"}</span>
          </label>
        )}

        <div className="ml-auto">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span>
                  <Button
                    onClick={handleSubmit}
                    disabled={exhausted || isSubmitting || (!text.trim() && !dataUrl)}
                    className="rounded-full"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />
                        Refining…
                      </>
                    ) : (
                      <>Refine{remaining < limit ? ` (${remaining} left)` : ""}</>
                    )}
                  </Button>
                </span>
              </TooltipTrigger>
              {exhausted && (
                <TooltipContent>Generate a new design to unlock more refinements.</TooltipContent>
              )}
            </Tooltip>
          </TooltipProvider>
        </div>
      </div>
    </div>
  );
};

export default RefinePanel;
